import Type from 'union-type'
import {
  Maybe,
  Wizard,
  Subjects,
  ChartSelections,
  ChartPattern, // eslint-disable-line
  ChartType, // eslint-disable-line
  Metrics, // eslint-disable-line
  MaybeCasePatternMap, // eslint-disable-line
} from '@domain'
import * as R from 'ramda'

export const state = {
  ...Wizard.WizardOf({
    chartType: Maybe.Nothing,
    chartPattern: Maybe.Nothing,
    chartSelection: Maybe.Nothing,
    metrics: Maybe.Nothing,
  }),
}

/** @type {MaybeCasePatternMap<ChartSelections, Maybe<string>>} */
let chartSelectionNextRoutePattern = {
  Nothing: () => Maybe.Just('breakdown'),
  Just: cSelection =>
    ChartSelections.case(
      {
        /**
         * @return {Maybe<string>}
         * */
        StateVsRegions: (state, regions) =>
          state.bind(_ =>
            regions.case({
              Nothing: () => Maybe.Just('choose-regions'),
              Just: v =>
                v.length > 0 ? Maybe.Nothing : Maybe.Just('choose-regions'),
            })
          ),
        Regions: regions =>
          regions.case({
            Nothing: () => Maybe.Just('choose-regions'),
            Just: v =>
              v.length > 0 ? Maybe.Nothing : Maybe.Just('choose-regions'),
          }),
        RegionVsDistricts: (region, districts) =>
          region.case({
            Nothing: () => Maybe.Just('choose-single-region'),
            Just: _ =>
              districts.case({
                Nothing: () => Maybe.Just('choose-districts'),
                Just: d =>
                  d.length > 0 ? Maybe.Nothing : Maybe.Just('choose-districts'),
              }),
          }),
        Districts: districts =>
          districts.case({
            Nothing: () => Maybe.Just('choose-districts'),
            Just: v =>
              v.length > 0 ? Maybe.Nothing : Maybe.Just('choose-districts'),
          }),
        DistrictVsCampuses: (district, campuses) =>
          district.case({
            Nothing: () => Maybe.Just('choose-single-district'),
            Just: _ =>
              campuses.case({
                Nothing: () => Maybe.Just('choose-campuses'),
                Just: v =>
                  v.length > 0 ? Maybe.Nothing : Maybe.Just('choose-campuses'),
              }),
          }),
        Campuses: campuses =>
          campuses.case({
            Nothing: () => Maybe.Just('choose-campuses'),
            Just: c =>
              c.length > 0 ? Maybe.Nothing : Maybe.Just('choose-campuses'),
          }),
      },
      cSelection
    ),
}

export const getters = {
  metricsRange: state => R.chain(cType => cType.getMetrics(), state.chartType),
  /**
   * @param {Wizard} state
   * @returns {string}
   */
  wizardNextRoute(state) {
    /** @type {Maybe<string>} */
    let nextRoute = Maybe.choose([
      () =>
        state.chartType.case({
          Nothing: () => Maybe.Just('choose-vis-type'),
          Just: _ => Maybe.Nothing,
        }),
      () =>
        state.chartPattern.case({
          Nothing: () => Maybe.Just('breakdown'),
          Just: _ => Maybe.Nothing,
        }),
      () => state.chartSelection.case(chartSelectionNextRoutePattern),
      () =>
        state.chartType.bind(ct =>
          ct.getMetrics().case({
            Nothing: () => Maybe.Just('/dataViz/reports'),
            Just: _ =>
              state.metrics.case({
                Nothing: () => Maybe.Just('choose-data-categories'),
                Just: v =>
                  Maybe.Just(
                    v.length > 0 ? '/dataViz/reports' : 'choose-data-categories'
                  ),
              }),
          })
        ),
    ])()
    return nextRoute.case({
      Just: nr => nr,
      Nothing: () => {
        throw new Error('fail to find next route')
      },
    })
  },
}

const CacheKey = '_wizard_state'

let cTypeLens = R.lens(R.prop('chartType'), (val, wizard) => {
  wizard.chartType = val
  if (Type.check) {
    Wizard.WizardOf(wizard) // type checking
  }
  return wizard
})
let cPatternLens = R.lens(R.prop('chartPattern'), (val, wizard) => {
  wizard.chartPattern = val
  if (Type.check) {
    Wizard.WizardOf(wizard) // type checking
  }
  return wizard
})
let cSelectionLens = R.lens(R.prop('chartSelection'), (val, wizard) => {
  wizard.chartSelection = val
  if (Type.check) {
    Wizard.WizardOf(wizard) // type checking
  }
  return wizard
})
let metricsLens = R.lens(R.prop('metrics'), (val, wizard) => {
  wizard.metrics = val
  if (Type.check) {
    Wizard.WizardOf(wizard) // type checking
  }
  return wizard
})

/**
 * @param {Wizard} state
 * @param {Maybe<ChartType>} mchartType
 */
function SetChartType(state, mchartType) {
  R.set(cTypeLens, mchartType, state)

  mchartType.case({
    Nothing: () => {
      SetChartPattern(state, Maybe.Nothing)
    },
    /** @param {ChartType} cType */
    Just: cType => {
      cType.getMetrics().case({
        Nothing: () => {
          SetMetrics(state, Maybe.Nothing)
        },
        /** @param {Array<Metrics>} metricsArr */
        Just: metricsArr => {
          state.metrics.case({
            Nothing: () => {},
            Just: metricsSelected => {
              // @ts-ignore
              let x = R.pluck('0', metricsArr)
              // @ts-ignore
              let y = R.pluck('0', metricsSelected)
              if (R.without(x, y).length > 0) {
                SetMetrics(state, Maybe.Nothing)
              }
            },
          })
        },
      })
    },
  })
  sessionStorage.setItem(CacheKey, JSON.stringify(state))
}

/**
 * @param {Wizard} state
 * @param {Maybe<ChartPattern>} mchartPattern
 */
function SetChartPattern(state, mchartPattern) {
  R.set(cPatternLens, mchartPattern, state)
  /**
   * @type {function(ChartSelections): void}
   */
  let prefill = cSelection => SetChartSelection(state, Maybe.Just(cSelection))

  mchartPattern.case({
    Nothing: () => {
      SetChartSelection(state, Maybe.Nothing)
    },
    Just: cPattern => {
      state.chartSelection.case({
        Nothing: () => prefill(defaultSelectionWithPattern(cPattern)),
        Just: cSelection => {
          // @ts-ignore
          if (cSelection._name !== cPattern._name) {
            prefill(defaultSelectionWithPattern(cPattern))
          }
        },
      })
    },
  })
  sessionStorage.setItem(CacheKey, JSON.stringify(state))
}

/**
 * @param {Wizard} state
 * @param {Maybe<ChartSelections>} mchartSelection
 */
function SetChartSelection(state, mchartSelection) {
  R.set(cSelectionLens, mchartSelection, state)
  SetMetrics(state, Maybe.Nothing)
  sessionStorage.setItem(CacheKey, JSON.stringify(state))
}

/**
 * @param {Wizard} state
 * @param {Maybe<Array<Metrics>>} mmetrics
 */
function SetMetrics(state, mmetrics) {
  R.set(metricsLens, mmetrics, state)
  sessionStorage.setItem(CacheKey, JSON.stringify(state))
}

let stupidInfer = types => {
  let typeMap = R.pipe(
    R.chain(t => R.map(k => [k, t], Object.keys(t))),
    R.fromPairs
  )(types)
  let infer = obj => {
    var typestr = typeof obj
    if (
      R.contains(typestr, ['string', 'number', 'boolean', 'function']) ||
      R.isNil(obj)
    ) {
      return obj
    } else if (Array.isArray(obj)) {
      return R.map(infer, obj)
    } else if (!!obj['_name'] && !!obj['_keys']) {
      let ctor =
        obj['_keys'].length === 0
          ? R.path([obj['_name'], obj['_name'] + 'Of'], typeMap)
          : R.path([obj['_name'], obj['_name']], typeMap)
      if (!ctor) {
        throw new Error('fail to retore state. can not infer type')
      }
      let args = R.pipe(
        R.props(obj['_keys']),
        R.map(infer)
      )(obj)
      return R.apply(ctor, args)
    } else {
      return obj
    }
  }
  return infer
}
let stateInferFns = {
  chartType: stupidInfer([Maybe, ChartType]),
  chartPattern: stupidInfer([Maybe, ChartPattern]),
  chartSelection: stupidInfer([Maybe, ChartSelections, Subjects]),
  metrics: stupidInfer([Maybe, Metrics]),
}

function TryRestoreState(state) {
  let raw = sessionStorage.getItem(CacheKey)
  var dehydrate
  try {
    dehydrate = JSON.parse(raw)
  } catch (e) {}

  if (!dehydrate) {
    return
  }

  state.chartType = stateInferFns.chartType(dehydrate.chartType)
  state.chartPattern = stateInferFns.chartPattern(dehydrate.chartPattern)
  state.chartSelection = stateInferFns.chartSelection(dehydrate.chartSelection)
  state.metrics = stateInferFns.metrics(dehydrate.metrics)
}

function ClearCachedState() {
  sessionStorage.removeItem(CacheKey)
}

export const mutations = {
  SetChartType,
  SetChartPattern,
  SetChartSelection,
  SetMetrics,
  TryRestoreState,
  ClearCachedState,
}

/**
 * @param {ChartPattern} cPattern
 * @returns {ChartSelections}
 */
function defaultSelectionWithPattern(cPattern) {
  let texas = Subjects.State

  return cPattern.case({
    StateVsRegions: _ =>
      ChartSelections.StateVsRegionsOf({
        state: Maybe.Just(texas),
        regions: Maybe.Nothing,
      }),
    Regions: _ => ChartSelections.Regions(Maybe.Nothing),
    RegionVsDistricts: _ =>
      ChartSelections.RegionVsDistrictsOf({
        region: Maybe.Nothing,
        districts: Maybe.Nothing,
      }),
    Districts: _ => ChartSelections.Districts(Maybe.Nothing),
    DistrictVsCampuses: _ =>
      ChartSelections.DistrictVsCampusesOf({
        district: Maybe.Nothing,
        campuses: Maybe.Nothing,
      }),
    Campuses: _ => ChartSelections.Campuses(Maybe.Nothing),
  })
}

export const actions = {
  //
}
